/**
 * Resolve uma imagem de cartão (front/back) que `CardMarkdown` encontrou num
 * token `![alt](url)` já aprovado por `isSafeImageSrc`.
 *
 * A maioria dos `src` seguros chega pronta para um `<img>` comum — http(s),
 * ou outro caminho absoluto público — e passa direto, sem leitura nenhuma.
 * Mas `/files/<chave>` é a rota autenticada de upload: um `<img src>` não
 * carrega `Authorization`, então bateria no dev server (sem proxy) ou
 * tomaria 401 da API real. É o mesmo problema que `OclusaoFace` já resolveu
 * para a foto do cartão de oclusão — mesma receita aqui: detectar o
 * prefixo, extrair a chave, e ler com `useAuthedImage` em vez de confiar
 * num `<img src>` cru. `resolveImageUrl` é a saída de story/preview: pula a
 * leitura de rede e resolve a URL de forma síncrona.
 */
import useAuthedImage from '../../../core/hooks/useAuthedImage';

export interface ImagemDoCartaoProps {
  /** Alt text from the markdown token. May be empty string. */
  alt: string;
  /** The already-safety-checked markdown image target (isSafeImageSrc already passed it). */
  src: string;
  /** Story override: when given, skips the authenticated fetch and resolves synchronously. Takes the extracted file key, not the full `/files/...` path. */
  resolveImageUrl?: (fileKey: string) => string;
}

type FaseImagem = 'carregando' | 'pronta' | 'falha';

/** Só um alvo `/files/<chave>` precisa da leitura autenticada; o resto passa direto. Exige ao menos um caractere depois do prefixo. */
const ROTA_ARQUIVO = /^\/files\/(.+)$/;

const CLASSE_IMAGEM = 'inline-block h-auto max-h-60 max-w-full rounded-input align-middle';

/** `loading` do hook começa falso; com chave e sem URL ainda, a primeira pintura já é `carregando`. */
function faseDaLeitura(temOverride: boolean, url: string | null, error: boolean): FaseImagem {
  if (temOverride) return 'pronta';
  if (error) return 'falha';
  if (!url) return 'carregando';
  return 'pronta';
}

/** Só a URL do override (síncrona) ou o object URL do hook. Nunca um path de `/files`. */
function enderecoPronto(
  fase: FaseImagem,
  fileKey: string,
  resolveImageUrl: ((fileKey: string) => string) | undefined,
  url: string | null,
): string | null {
  if (fase !== 'pronta') return null;
  if (resolveImageUrl) return resolveImageUrl(fileKey);
  return url;
}

export default function ImagemDoCartao({ alt, src, resolveImageUrl }: ImagemDoCartaoProps): JSX.Element {
  const match = ROTA_ARQUIVO.exec(src);
  const fileKey = match ? match[1] : undefined;

  // Chamada incondicional do hook — nunca dentro de um `if` — mesmo quando o
  // resultado não vai ser usado (fora de /files, ou com override de story).
  const pularLeitura = fileKey === undefined || resolveImageUrl !== undefined;
  const { url, error } = useAuthedImage(pularLeitura ? undefined : fileKey);

  if (fileKey === undefined) {
    // http(s) ou outro caminho absoluto fora de /files — o <img> de sempre, inalterado.
    return <img alt={alt} src={src} className={CLASSE_IMAGEM} draggable={false} />;
  }

  const fase = faseDaLeitura(resolveImageUrl !== undefined, url, error);
  const endereco = enderecoPronto(fase, fileKey, resolveImageUrl, url);

  if (fase === 'pronta' && endereco !== null) {
    return <img alt={alt} src={endereco} className={CLASSE_IMAGEM} draggable={false} />;
  }

  if (fase === 'falha') {
    return (
      <span className="inline-flex h-40 w-40 items-center justify-center rounded-input bg-surface-muted px-2 text-center align-middle">
        <span className="text-xs leading-tight text-danger-ink">Não foi possível carregar a imagem.</span>
      </span>
    );
  }

  return (
    <span
      aria-busy="true"
      className="inline-block h-40 w-40 rounded-input bg-surface-muted align-middle"
    />
  );
}
